import {
  AdminLearningContentRow,
  AdminLearningContentRowUpsert,
  AdminLearningContentSubCategory,
  AdminLearningContentTemplate,
} from '../types/domain';
import { supabase } from '../supabaseClient';
import {
  clearAdminLearningContentRowsCache,
  loadAdminLearningContentRowsCacheAsync,
  loadAdminLearningContentTemplatesCacheAsync,
  patchAdminLearningContentTemplatesCache,
  renameAdminLearningContentRowsCache,
  saveAdminLearningContentRowsCache,
  saveAdminLearningContentTemplatesCache,
  touchAdminLearningContentRowsHeadChecked,
  touchAdminLearningContentTemplatesHeadChecked,
} from '../utils/adminLearningContentCache';
import {
  isAdminLearningContentRowsSessionDbSynced,
  isAdminLearningContentTemplatesSessionDbSynced,
  markAdminLearningContentRowsSessionDbSynced,
  markAdminLearningContentTemplatesSessionDbSynced,
} from '../utils/adminLearningContentSessionSync';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  buildLearningContentScopeVersion,
  learningContentScopeVersionFromHead,
} from '../utils/learningContentCacheVersion';

const ADMIN_LEARNING_CONTENT_SELECT = [
  'id',
  'workspace_id',
  'grade_number',
  'sub_category',
  'template_name',
  'order_index',
  'publisher',
  'track_text',
  'seq_text',
  'unit_text',
  'learning_text',
  'page_text',
  'assistant_page_text',
  'target_lesson_text',
  'total_lesson_text',
  'material_text',
  'achievement_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'created_at',
  'updated_at',
].join(', ');

async function fetchAdminLearningContentRowsHead(
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): Promise<string> {
  const scopeTemplate = templateName.trim();
  const { count, error: countError } = await supabase
    .from('admin_learning_content_rows')
    .select('id', { count: 'exact', head: true })
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate);
  if (countError) throw new Error(countError.message);
  if (!count) return learningContentScopeVersionFromHead(0, '');
  const { data, error } = await supabase
    .from('admin_learning_content_rows')
    .select('updated_at')
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return learningContentScopeVersionFromHead(count, maxUpdatedAt);
}

/**
 * 플랫폼 전역 관리자 학습내용(`workspace_id is null`).
 * `workspaceId` 인자는 하위 호환·캐시 API 형태 유지용이며 DB 필터에는 쓰이지 않습니다.
 * 본문: IndexedDB(+메모리). 메타: LS contentVersion.
 * head(`count:max(updated_at)`)는 로그인 후 해당 스코프를 처음 열 때만 한다.
 * 같은 로그인에서 페이지를 다시 열면 캐시를 재사용하고, 로그아웃·재로그인 후 첫 조회에서 다시 비교한다.
 */
export async function getAdminLearningContentRows(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  options?: { forceRefresh?: boolean }
): Promise<AdminLearningContentRow[]> {
  if (!templateName.trim()) return [];
  const scopeTemplate = templateName.trim();
  const cached = await loadAdminLearningContentRowsCacheAsync(
    workspaceId,
    gradeNumber,
    subCategory,
    scopeTemplate
  );
  if (!options?.forceRefresh && cached.found && cached.contentVersion) {
    const sessionOk = isAdminLearningContentRowsSessionDbSynced(
      gradeNumber,
      subCategory,
      scopeTemplate
    );
    if (sessionOk) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('학습내용관리·행·IDB');
      }
      return cached.rows;
    }
    try {
      const headVersion = await fetchAdminLearningContentRowsHead(
        gradeNumber,
        subCategory,
        scopeTemplate
      );
      if (cached.contentVersion === headVersion) {
        touchAdminLearningContentRowsHeadChecked(
          gradeNumber,
          subCategory,
          scopeTemplate,
          cached.contentVersion
        );
        markAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, scopeTemplate);
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('학습내용관리·행·IDB');
        }
        return cached.rows;
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  } else if (
    !options?.forceRefresh &&
    cached.found &&
    isAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, scopeTemplate)
  ) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('학습내용관리·행·IDB');
    }
    return cached.rows;
  }
  const { data, error } = await supabase
    .from('admin_learning_content_rows')
    .select(ADMIN_LEARNING_CONTENT_SELECT)
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown) as AdminLearningContentRow[];
  const contentVersion = buildLearningContentScopeVersion(rows);
  saveAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, scopeTemplate, rows, {
    force: true,
    contentVersion,
  });
  markAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, scopeTemplate);
  return rows;
}

export async function replaceAdminLearningContentRows(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  rows: AdminLearningContentRowUpsert[]
): Promise<AdminLearningContentRow[]> {
  if (!templateName.trim()) return [];
  const scopeTemplate = templateName.trim();
  const now = new Date().toISOString();
  const { error: deleteError } = await supabase
    .from('admin_learning_content_rows')
    .delete()
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', scopeTemplate);
  if (deleteError) throw new Error(deleteError.message);
  if (!rows.length) {
    saveAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, scopeTemplate, [], { force: true });
    markAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, scopeTemplate);
    return [];
  }
  const payload = rows.map(({ id: _ignored, workspace_id: _w, ...row }, idx) => ({
    ...row,
    workspace_id: null,
    template_name: scopeTemplate,
    order_index: Number.isInteger(row.order_index) ? row.order_index : idx,
    /** 클라이언트에서 null/undefined가 섞여도 INSERT 시 NOT NULL 위반 방지 */
    created_at: now,
    updated_at: now,
  }));
  const { data, error } = await supabase
    .from('admin_learning_content_rows')
    .insert(payload)
    .select(ADMIN_LEARNING_CONTENT_SELECT)
    .order('order_index', { ascending: true });
  if (error) throw new Error(error.message);
  const saved = ((data ?? []) as unknown) as AdminLearningContentRow[];
  saveAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, scopeTemplate, saved, { force: true });
  markAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, scopeTemplate);
  return saved;
}

const ADMIN_LEARNING_TEMPLATE_SELECT = [
  'id',
  'workspace_id',
  'grade_number',
  'sub_category',
  'template_name',
  'order_index',
  'created_at',
  'updated_at',
].join(', ');

async function fetchAdminLearningContentTemplatesHead(
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory
): Promise<string> {
  const { count, error: countError } = await supabase
    .from('admin_learning_content_templates')
    .select('id', { count: 'exact', head: true })
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory);
  if (countError) throw new Error(countError.message);
  if (!count) return learningContentScopeVersionFromHead(0, '');
  const { data, error } = await supabase
    .from('admin_learning_content_templates')
    .select('updated_at')
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return learningContentScopeVersionFromHead(count, maxUpdatedAt);
}

export async function getAdminLearningContentTemplates(
  workspaceId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  options?: { forceRefresh?: boolean }
): Promise<AdminLearningContentTemplate[]> {
  const cached = await loadAdminLearningContentTemplatesCacheAsync(
    workspaceId,
    gradeNumber,
    subCategory
  );
  if (!options?.forceRefresh && cached.found && cached.contentVersion) {
    const sessionOk = isAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
    if (sessionOk) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('학습내용관리·편제목록·IDB');
      }
      return cached.templates;
    }
    try {
      const headVersion = await fetchAdminLearningContentTemplatesHead(gradeNumber, subCategory);
      if (cached.contentVersion === headVersion) {
        touchAdminLearningContentTemplatesHeadChecked(
          gradeNumber,
          subCategory,
          cached.contentVersion
        );
        markAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('학습내용관리·편제목록·IDB');
        }
        return cached.templates;
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  } else if (
    !options?.forceRefresh &&
    cached.found &&
    isAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory)
  ) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('학습내용관리·편제목록·IDB');
    }
    return cached.templates;
  }
  const { data, error } = await supabase
    .from('admin_learning_content_templates')
    .select(ADMIN_LEARNING_TEMPLATE_SELECT)
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const list = ((data ?? []) as unknown) as AdminLearningContentTemplate[];
  const contentVersion = buildLearningContentScopeVersion(list);
  saveAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory, list, {
    force: true,
    contentVersion,
  });
  markAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
  return list;
}

export async function createAdminLearningContentTemplate(params: {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, templateName } = params;
  const name = templateName.trim();
  if (!name) return;
  const now = new Date().toISOString();
  const { data: existing } = await supabase
    .from('admin_learning_content_templates')
    .select('id')
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', name)
    .maybeSingle();
  if (existing?.id) return;
  const { data: maxRow } = await supabase
    .from('admin_learning_content_templates')
    .select('order_index')
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .order('order_index', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrderIndex = typeof maxRow?.order_index === 'number' ? maxRow.order_index + 1 : 0;
  const { error } = await supabase.from('admin_learning_content_templates').insert({
    workspace_id: null,
    grade_number: gradeNumber,
    sub_category: subCategory,
    template_name: name,
    order_index: nextOrderIndex,
    updated_at: now,
  });
  if (error) throw new Error(error.message);
  await patchAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory, (list) => {
    if (list.some((row) => row.template_name.trim() === name)) return list;
    const maxOrder = list.reduce((max, row) => Math.max(max, row.order_index ?? 0), -1);
    return [
      ...list,
      {
        id: `local-${name}`,
        workspace_id: null,
        grade_number: gradeNumber,
        sub_category: subCategory,
        template_name: name,
        order_index: maxOrder + 1,
        created_at: now,
        updated_at: now,
      },
    ];
  });
  markAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
}

export async function renameAdminLearningContentTemplate(params: {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  fromTemplateName: string;
  toTemplateName: string;
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, fromTemplateName, toTemplateName } = params;
  const fromName = fromTemplateName.trim();
  const toName = toTemplateName.trim();
  if (!fromName || !toName || fromName === toName) return;
  const now = new Date().toISOString();
  const { data: duplicate } = await supabase
    .from('admin_learning_content_templates')
    .select('id')
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', toName)
    .maybeSingle();
  if (duplicate?.id) {
    throw new Error('이미 같은 이름의 편제가 있습니다.');
  }
  const { error: templateUpdateError } = await supabase
    .from('admin_learning_content_templates')
    .update({ template_name: toName, updated_at: now })
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', fromName);
  if (templateUpdateError) throw new Error(templateUpdateError.message);
  const { error: rowsError } = await supabase
    .from('admin_learning_content_rows')
    .update({ template_name: toName, updated_at: now })
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', fromName);
  if (rowsError) throw new Error(rowsError.message);

  await patchAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory, (list) =>
    list.map((row) => {
      const trimmed = row.template_name.trim();
      if (trimmed !== fromName) return row;
      return { ...row, template_name: toName, updated_at: now };
    })
  );
  await renameAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, fromName, toName);
  markAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
  markAdminLearningContentRowsSessionDbSynced(gradeNumber, subCategory, toName);
}

export async function reorderAdminLearningContentTemplates(params: {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  orderedTemplateNames: string[];
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, orderedTemplateNames } = params;
  const names = orderedTemplateNames.map((name) => name.trim()).filter(Boolean);
  if (!names.length) return;
  const { data, error } = await supabase
    .from('admin_learning_content_templates')
    .select('id, template_name, order_index')
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ id: string; template_name: string; order_index: number }>;
  const byName = new Map(rows.map((row) => [row.template_name.trim(), row]));
  const allNamesInCurrentOrder = [...rows]
    .sort((a, b) => a.order_index - b.order_index || a.id.localeCompare(b.id))
    .map((row) => row.template_name.trim());
  const used = new Set(names);
  const mergedNames = [...names, ...allNamesInCurrentOrder.filter((name) => !used.has(name))];

  const updates: Array<{ id: string; orderIndex: number; currentOrderIndex: number }> = [];
  mergedNames.forEach((name, idx) => {
    const row = byName.get(name);
    if (!row) return;
    if (row.order_index === idx) return;
    updates.push({ id: row.id, orderIndex: idx, currentOrderIndex: row.order_index });
  });
  if (!updates.length) return;

  // 1) 충돌 회피: 일단 모두 큰 임시 index로 이동
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_learning_content_templates')
      .update({ order_index: update.currentOrderIndex + 1000, updated_at: new Date().toISOString() })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  // 2) 최종 순번 반영
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_learning_content_templates')
      .update({ order_index: update.orderIndex, updated_at: new Date().toISOString() })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  await patchAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory, (list) => {
    const byName = new Map(list.map((row) => [row.template_name.trim(), row]));
    const next: AdminLearningContentTemplate[] = [];
    mergedNames.forEach((name, idx) => {
      const row = byName.get(name);
      if (!row) return;
      next.push({
        ...row,
        order_index: idx,
        updated_at: new Date().toISOString(),
      });
    });
    return next;
  });
  markAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
}

export async function deleteAdminLearningContentTemplate(params: {
  workspaceId: string;
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6;
  subCategory: AdminLearningContentSubCategory;
  templateName: string;
}): Promise<void> {
  const { workspaceId, gradeNumber, subCategory, templateName } = params;
  const name = templateName.trim();
  if (!name) return;

  const { error: deleteRowsError } = await supabase
    .from('admin_learning_content_rows')
    .delete()
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', name);
  if (deleteRowsError) throw new Error(deleteRowsError.message);

  const { error: deleteTemplateError } = await supabase
    .from('admin_learning_content_templates')
    .delete()
    .is('workspace_id', null)
    .eq('grade_number', gradeNumber)
    .eq('sub_category', subCategory)
    .eq('template_name', name);
  if (deleteTemplateError) throw new Error(deleteTemplateError.message);

  await patchAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory, (list) =>
    list.filter((row) => row.template_name.trim() !== name)
  );
  clearAdminLearningContentRowsCache(workspaceId, gradeNumber, subCategory, name);
  markAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory);
}
